<script setup lang="ts">
import type { ButtonColor } from '@/types/ui/variants';
import { computed } from 'vue';
import LoadingSpinner from './loading-spinner.vue';

export interface BaseButtonProps {
   type?: 'button' | 'submit' | 'reset';
   variant?: 'solid' | 'outline' | 'ghost';
   color?: ButtonColor;
   size?: 'xs' | 'sm' | 'md';
   stretch?: 'sm' | 'md' | 'lg';
   disabled?: boolean;
   loading?: boolean;
   fullWidth?: boolean;
   square?: boolean;
}

const props = withDefaults(defineProps<BaseButtonProps>(), {
   type: 'button',
   variant: 'solid',
   color: 'primary',
   size: 'md',
   stretch: 'md',
   disabled: false,
   loading: false,
   fullWidth: false,
});

const buttonClasses = computed(() => {
   // Base classes that apply to all buttons
   const baseClasses = [
      'transition-colors duration-200',
      'disabled:opacity-50 disabled:cursor-not-allowed',
   ];

   // Horizontal padding based on stretch prop
   // stretch: sm | md (default) | lg
   const stretchPadding = {
      xs: {
         sm: 'px-2',
         md: 'px-4', // current
         lg: 'px-6',
      },
      sm: {
         sm: 'px-3',
         md: 'px-5', // current
         lg: 'px-7',
      },
      md: {
         sm: 'px-5',
         md: 'px-7', // current
         lg: 'px-9',
      },
   };

   // Size classes (vertical padding and text size)
   const sizeClasses = {
      xs: `min-h-4 py-1 text-xs`,
      sm: `min-h-10 py-1.5 text-base`,
      md: `min-h-12 py-2 text-base`,
   };

   const squareSizeClasses = {
      xs: 'flex size-6 justify-center items-center rounded-lg',
      sm: 'text-base',
      md: 'text-base',
   };

   // Add size class and stretch padding
   if (props.square) {
      baseClasses.push(squareSizeClasses[props.size]);
   }
   else {
      baseClasses.push(sizeClasses[props.size]);
      baseClasses.push(stretchPadding[props.size][props.stretch]);
      baseClasses.push('rounded-full');
   }

   // Full width class
   if (props.fullWidth) {
      baseClasses.push('w-full');
   }

   // Handle loading state
   if (props.loading) {
      baseClasses.push('relative opacity-80 cursor-wait');
   }

   if (props.size === 'xs' && props.variant === 'outline') {
      baseClasses.push('border');
   }
   else if (props.variant === 'outline') {
      baseClasses.push('border-2');
   }

   if (props.size === 'xs') {
      baseClasses.push('font-normal');
   }
   else {
      baseClasses.push('font-semibold');
   }

   // Combined variant and color classes
   const variantColorClasses = {
      // Solid variant
      solid: {
         primary: 'bg-primary text-white hover:bg-primary-dark',
         secondary: 'bg-secondary text-white hover:bg-secondary-dark ',
         dark: 'bg-black text-white hover:bg-black/75 ',
         danger: 'bg-red-500 text-white hover:bg-red-600',
         white: 'bg-white text-black hover:bg-black/10 ',
         gradient: '', // Special handling in template
         luma: 'bg-primary-dark text-white hover:bg-primary',
      },
      // Outline variant
      outline: {
         primary: 'bg-transparent border-primary text-primary hover:bg-primary/10 ',
         secondary: 'bg-transparent border-secondary text-secondary hover:bg-secondary/10 ',
         dark: 'bg-transparent border-black text-black hover:bg-black/10 ',
         danger: 'bg-transparent border-red-500 text-red-500 hover:bg-red-500/10 ',
         white: 'bg-transparent border-neutral text-black hover:bg-black/10 ',
         gradient: '', // Special handling in template
         luma: 'bg-transparent border-primary-light text-primary-light hover:bg-primary-light/10',
      },
      // Ghost variant
      ghost: {
         primary: 'bg-transparent text-primary hover:bg-primary/10',
         secondary: 'bg-transparent text-secondary hover:bg-secondary/10 ',
         dark: 'bg-transparent text-black hover:text-black hover:bg-black/10 ',
         danger: 'bg-transparent text-red-500 hover:bg-red-500/10 ',
         white: 'bg-transparent text-black hover:bg-black/10 ',
         gradient: '', // Special handling in template
         luma: 'bg-transparent text-primary-light hover:bg-primary-light/10',
      },
   };

   // Add the variant + color classes (skip for gradient as it has special template handling)
   if (props.color !== 'gradient') {
      baseClasses.push(variantColorClasses[props.variant][props.color]);
   }
   else {
      // For gradient, add relative positioning and group classes
      baseClasses.push('relative group');
   }

   return baseClasses.join(' ');
});
</script>

<template>
   <button
      :type="type"
      :disabled="disabled || loading"
      :class="buttonClasses"
   >
      <!-- Gradient button special structure -->
      <template v-if="color === 'gradient'">
         <!-- Gradient border background -->
         <span
            v-if="variant === 'outline'"
            class="absolute inset-0 rounded-full bg-gradient-primary"
         />
         <!-- White background with hover effect -->
         <span
            class="absolute inset-[2px] rounded-full transition-opacity duration-200"
            :class="variant === 'solid'
               ? 'bg-gradient-primary group-hover:brightness-75'
               : 'bg-white group-hover:opacity-0'"
         />
         <!-- Button content with gradient text -->
         <span
            v-if="loading"
            class="relative flex items-center justify-center font-semibold z-10 transition-all duration-200 text-white"
         >
            <LoadingSpinner :square="square" />
            <span v-if="!square">
               Loading...
            </span>
         </span>
         <span
            v-else
            class="relative flex items-center justify-center font-semibold z-10 transition-all duration-200"
            :class="variant === 'solid'
               ? 'text-white'
               : 'bg-gradient-primary bg-clip-text text-transparent group-hover:text-white'"
         >
            <slot />
         </span>
      </template>

      <!-- Regular button structure -->
      <template v-else>
         <span v-if="loading" class="inline-flex items-center">
            <LoadingSpinner :square="square" />
            <span v-if="!square">
               Loading...
            </span>
         </span>
         <slot v-else />
      </template>
   </button>
</template>
